import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/auth.jsx'
import { api } from '../lib/api.js'

function Pill({ children }) {
  return (
    <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/70">
      {children}
    </span>
  )
}

function Card({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
      <div className="text-xs tracking-[0.28em] text-white/50">{label}</div>
      <div className="mt-2 text-sm text-white/70 break-all">{value}</div>
    </div>
  )
}

export default function Account() {
  const { user, token, loading, login, register, refresh, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Inline auth form (so /account works even without using the navbar modal)
  const [tab, setTab] = React.useState('login')
  const [handle, setHandle] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [authErr, setAuthErr] = React.useState('')
  const [authBusy, setAuthBusy] = React.useState(false)


  // If we returned from Discord OAuth, refresh the user then clean the URL.
  React.useEffect(() => {
    const qs = new URLSearchParams(location.search || '')
    const discord = qs.get('discord')
    if (!discord) return
    ;(async () => {
      try { await refresh?.() } catch {}
      // remove query so refresh doesn't loop
      navigate('/account', { replace: true })
    })()
  }, [location.search])

  // Beta / Entitlements / Discord
  const [channels, setChannels] = React.useState([])
  const [redeemCode, setRedeemCode] = React.useState('')
  const [msg, setMsg] = React.useState('')
  const [err, setErr] = React.useState('')
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    let live = true
    ;(async () => {
      if (!token) {
        if (live) setChannels([])
        return
      }
      try {
        const d = await api('/api/entitlements', { token })
        if (live) setChannels(Array.isArray(d?.channels) ? d.channels : [])
      } catch {
        // ignore
      }
    })()
    return () => { live = false }
  }, [token])

  async function onAuthSubmit(e) {
    e.preventDefault()
    setAuthErr('')
    setAuthBusy(true)
    try {
      if (tab === 'login') {
        await login(email, password)
      } else {
        await register(handle, email, password)
      }
      setEmail('')
      setPassword('')
      setHandle('')
    } catch (e2) {
      setAuthErr(e2?.message || 'Auth failed.')
    } finally {
      setAuthBusy(false)
    }
  }

  async function redeem() {
    setErr('')
    setMsg('')
    setBusy(true)
    try {
      const d = await api('/api/beta/redeem', { method: 'POST', token, body: { code: redeemCode } })
      setMsg(`Unlocked channel: ${d.channel}`)
      setRedeemCode('')
      const e = await api('/api/entitlements', { token })
      setChannels(Array.isArray(e?.channels) ? e.channels : [])
    } catch (e) {
      setErr(e?.message || 'Redeem failed.')
    } finally {
      setBusy(false)
    }
  }

  async function startDiscordLink() {
    setErr('')
    setMsg('')
    setBusy(true)
    try {
      if (!token) throw new Error('Please login first.')
      const d = await api('/api/discord/link/start', { token })
      if (!d?.url) throw new Error('Discord link URL not returned.')
      window.location.href = d.url
    } catch (e) {
      setErr(e?.message || 'Discord link failed.')
      setBusy(false)
    }
  }

  async function unlinkDiscord() {
    setErr('')
    setMsg('')
    setBusy(true)
    try {
      if (!token) throw new Error('Please login first.')
      await api('/api/discord/unlink', { method: 'POST', token })
      setMsg('Discord unlinked. Refreshing…')
      // refresh auth user
      const me = await api('/api/auth/me', { token })
      // useAuth updates on token change; simplest is reload
      window.location.reload()
    } catch (e) {
      setErr(e?.message || 'Unlink failed.')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div className="mx-auto max-w-6xl px-4 py-12 text-white/60">Loading account…</div>
  }

  // Not logged in: show inline auth
  if (!user) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-12">
        <div className="text-xs tracking-[0.32em] text-white/60">ACCOUNT</div>
        <h1 className="mt-2 text-3xl md:text-5xl font-semibold">Login / Register</h1>
        <p className="mt-4 text-white/60 max-w-2xl">
          Create an account to redeem beta keys, link Discord, and unlock download channels.
        </p>

        <div className="mt-8 flex gap-2">
          <button
            onClick={() => setTab('login')}
            className={
              'rounded-xl px-4 py-2 text-sm border ' +
              (tab === 'login'
                ? 'border-white/25 bg-white/10 text-white'
                : 'border-white/10 bg-black/20 text-white/70 hover:border-white/20')
            }
          >
            Login
          </button>
          <button
            onClick={() => setTab('register')}
            className={
              'rounded-xl px-4 py-2 text-sm border ' +
              (tab === 'register'
                ? 'border-white/25 bg-white/10 text-white'
                : 'border-white/10 bg-black/20 text-white/70 hover:border-white/20')
            }
          >
            Register
          </button>
        </div>

        <form onSubmit={onAuthSubmit} className="mt-6 max-w-xl rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          {tab === 'register' ? (
            <div className="mb-4">
              <label className="block text-xs tracking-[0.28em] text-white/50">HANDLE</label>
              <input
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="INKED"
                className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/80 outline-none focus:border-white/20"
              />
            </div>
          ) : null}

          <div className="mb-4">
            <label className="block text-xs tracking-[0.28em] text-white/50">EMAIL</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              type="email"
              className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/80 outline-none focus:border-white/20"
            />
          </div>

          <div className="mb-2">
            <label className="block text-xs tracking-[0.28em] text-white/50">PASSWORD</label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              type="password"
              className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/80 outline-none focus:border-white/20"
            />
          </div>

          {authErr ? (
            <div className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-white/80">
              {authErr}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={authBusy}
            className="mt-5 w-full rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15 disabled:opacity-60"
          >
            {authBusy ? 'Please wait…' : tab === 'login' ? 'Login' : 'Create account'}
          </button>
        </form>
      </div>
    )
  }

  // Logged in
  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-xs tracking-[0.32em] text-white/60">ACCOUNT</div>
          <h1 className="mt-2 text-3xl md:text-5xl font-semibold">Welcome, {user.handle}</h1>
          <p className="mt-3 text-white/60 max-w-3xl">
            Redeem beta keys, link Discord for verification, and manage your early-access status.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={logout}
            className="rounded-xl border border-white/15 bg-black/20 px-4 py-2 text-sm hover:bg-white/5"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card label="Email" value={user.email} />
        <Card label="Created" value={user.createdAt} />
        <Card label="Role" value={user.role} />
      </div>

      <section className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.32em] text-white/60">BETA ACCESS</div>
          <div className="mt-2 text-lg font-semibold">Redeem a beta / invite code</div>
          <div className="mt-2 text-sm text-white/60">
            Codes unlock download channels (e.g., beta builds, internal test branches).
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value)}
              placeholder="ENTER CODE"
              className="flex-1 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white/80 outline-none focus:border-white/20"
            />
            <button
              onClick={redeem}
              disabled={busy || !redeemCode}
              className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15 disabled:opacity-60"
            >
              Redeem
            </button>
          </div>

          {msg ? (
            <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-white/80">
              {msg}
            </div>
          ) : null}
          {err ? (
            <div className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-white/80">
              {err}
            </div>
          ) : null}

          <div className="mt-6">
            <div className="text-xs tracking-[0.28em] text-white/50">UNLOCKED CHANNELS</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {channels.length ? channels.map((c) => <Pill key={c}>{c}</Pill>) : <Pill>none</Pill>}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-panel/60 glow p-6">
          <div className="text-xs tracking-[0.32em] text-white/60">DISCORD</div>
          <div className="mt-2 text-lg font-semibold">Link your Discord account</div>
          <div className="mt-2 text-sm text-white/60">
            Linking lets you verify roles, whitelist testers, and grant in-server access automatically.
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <button
              onClick={startDiscordLink}
              disabled={busy}
              className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm hover:bg-white/15 disabled:opacity-60"
            >
              {busy ? 'Working…' : user.discordId ? 'Re-link Discord' : 'Link Discord'}
            </button>

            {user.discordId ? (
              <button
                onClick={unlinkDiscord}
                disabled={busy}
                className="rounded-xl border border-white/15 bg-black/20 px-5 py-3 text-sm hover:bg-white/5 disabled:opacity-60"
              >
                Unlink
              </button>
            ) : null}
          </div>

          {user.discordId ? (
            <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/70">
              Linked as <span className="text-white/85">{user.discordUsername || user.discordId}</span>
              {user.discordLinkedAt ? (
                <div className="mt-1 text-white/50 text-xs">Linked at: {user.discordLinkedAt}</div>
              ) : null}
            </div>
          ) : (
            <div className="mt-5 rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/70">
              Not linked yet.
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
